import React, { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Trophy, RotateCcw } from "lucide-react";

interface SlidingPuzzleProps {
  t: (key: string) => string;
}

const GRID_SIZE = 3;
const TOTAL_TILES = GRID_SIZE * GRID_SIZE;

const createSolvedPuzzle = (): number[] => {
  const tiles: number[] = [];
  for (let i = 1; i < TOTAL_TILES; i++) {
    tiles.push(i);
  }
  tiles.push(0);
  return tiles;
};

const isSolvable = (tiles: number[]): boolean => {
  let inversions = 0;
  const filtered = tiles.filter(t => t !== 0);
  for (let i = 0; i < filtered.length; i++) {
    for (let j = i + 1; j < filtered.length; j++) {
      if (filtered[i] > filtered[j]) {
        inversions++;
      }
    }
  }
  return inversions % 2 === 0;
};

const shufflePuzzle = (): number[] => {
  let tiles: number[];
  do {
    tiles = [...createSolvedPuzzle()].sort(() => Math.random() - 0.5);
  } while (!isSolvable(tiles) || isSolved(tiles));
  return tiles;
};

const isSolved = (tiles: number[]): boolean => {
  const solved = createSolvedPuzzle();
  return tiles.every((tile, index) => tile === solved[index]);
};

const canMove = (index: number, emptyIndex: number): boolean => {
  const row = Math.floor(index / GRID_SIZE);
  const col = index % GRID_SIZE;
  const emptyRow = Math.floor(emptyIndex / GRID_SIZE);
  const emptyCol = emptyIndex % GRID_SIZE;
  
  return (
    (row === emptyRow && Math.abs(col - emptyCol) === 1) ||
    (col === emptyCol && Math.abs(row - emptyRow) === 1)
  );
};

export function SlidingPuzzle({ t }: SlidingPuzzleProps) {
  const [tiles, setTiles] = useState<number[]>(shufflePuzzle);
  const [moves, setMoves] = useState(0);
  const [won, setWon] = useState(false);

  const handleTileClick = useCallback((index: number) => {
    if (won) return;
    
    const emptyIndex = tiles.indexOf(0);
    if (!canMove(index, emptyIndex)) return;
    
    const newTiles = [...tiles];
    [newTiles[index], newTiles[emptyIndex]] = [newTiles[emptyIndex], newTiles[index]];
    setTiles(newTiles);
    setMoves(m => m + 1);
    
    if (isSolved(newTiles)) {
      setWon(true);
    }
  }, [tiles, won]);

  const resetGame = useCallback(() => {
    setTiles(shufflePuzzle());
    setMoves(0);
    setWon(false);
  }, []);

  const emptyIndex = tiles.indexOf(0);

  return (
    <div className="space-y-6" dir="ltr">
      <div className="flex items-center justify-between">
        <div className="text-lg text-muted-foreground">{t("moves")}: {moves}</div>
        <Button
          onClick={resetGame}
          variant="outline"
          className="gap-2"
          data-scan-item
        >
          <RotateCcw className="w-4 h-4" />
          {t("newGame")}
        </Button>
      </div>

      {won && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="flex items-center justify-center gap-3 p-4 bg-success/20 rounded-xl border border-success"
        >
          <Trophy className="w-6 h-6 text-success" />
          <span className="text-xl font-bold text-success">
            {t("wonInMoves").replace("{0}", String(moves))}
          </span>
        </motion.div>
      )}

      <div className="max-w-xs mx-auto">
        <div className="grid grid-cols-3 gap-2 bg-muted/30 p-3 rounded-xl">
          {tiles.map((tile, index) => {
            const movable = canMove(index, emptyIndex);

            return (
              <motion.button
                key={index}
                onClick={() => handleTileClick(index)}
                whileHover={movable && !won ? { scale: 1.05 } : {}}
                whileTap={movable && !won ? { scale: 0.95 } : {}}
                disabled={tile === 0 || !movable || won}
                className={cn(
                  "aspect-square rounded-xl text-2xl font-bold flex items-center justify-center",
                  "transition-all duration-200 border-2",
                  tile === 0
                    ? "bg-transparent border-transparent"
                    : movable
                      ? "bg-primary text-primary-foreground border-primary hover:bg-primary/90 cursor-pointer"
                      : "bg-muted text-muted-foreground border-border cursor-not-allowed"
                )}
                {...(tile !== 0 ? { "data-scan-item": true } : {})}
              >
                {tile > 0 && tile}
              </motion.button>
            );
          })}
        </div>
      </div>

      <p className="text-center text-sm text-muted-foreground">
        {t("slidingPuzzleHint")}
      </p>
    </div>
  );
}
